import { Dispatch, SetStateAction, useCallback, useMemo } from "react";
import ChipDropdownMenu from "components/OrderSearch/ChipDropdownMenu.tsx";
import { FilterType } from "components/OrderSearch/filterType.ts";
import * as Style from "./MarketingMaterialsFilters.styles.ts";
import { MarketingMaterialsFilterOpts } from "../MarketingMaterialsToolbar/MarketingMaterialsToolbar.tsx";
import MarketingMaterialsBrandProductLineFilter from "../MarketingMaterialsBrandProductLineFilter/MarketingMaterialsBrandProductLineFilter.tsx";
import { BrandWithProductLines } from "../../../MockModels/MockBrandAndProductLine.ts";
import { InputLabel, MenuItem, Select, SelectChangeEvent } from "@mui/material";
import { SORT_OPTIONS, SortOption } from "../../marketingMaterialsConstants.ts";
import { models } from "types/api/viewModels.ts";

interface MarketingMaterialsFiltersProps {
	filterOptions: MarketingMaterialsFilterOpts;
	setFilterOptions: Dispatch<SetStateAction<MarketingMaterialsFilterOpts>>;
	merchandiseFilters?: models["MerchandiseProductFilterDto"] | null;
	onFiltersChanged: (nextFilterOptions: MarketingMaterialsFilterOpts) => void;
}

const MarketingMaterialsFilters = ({
	filterOptions,
	setFilterOptions,
	merchandiseFilters,
	onFiltersChanged
}: MarketingMaterialsFiltersProps) => {
	const getFilterNames = (filters?: models["FilterOptionDto"][] | null) =>
		(filters?.map((filter) => filter.name).filter((name): name is string => Boolean(name)) ?? []).sort((a, b) =>
			a.localeCompare(b)
		);

	const onFilterChange = useCallback(
		(id: string, values: string[]) => {
			const nextFilterOptions = {
				...filterOptions,
				[id]: values
			};

			setFilterOptions(nextFilterOptions);
			onFiltersChanged(nextFilterOptions);
		},
		[filterOptions, setFilterOptions, onFiltersChanged]
	);

	const getFilterId = (type: string) => `marketing-materials-${type}-filter-chip`;

	const channelItems = useMemo(
		() => getFilterNames(merchandiseFilters?.channelFilter),
		[merchandiseFilters?.channelFilter]
	);
	const categoryItems = useMemo(
		() => getFilterNames(merchandiseFilters?.categoryFilter),
		[merchandiseFilters?.categoryFilter]
	);
	const typeItems = useMemo(() => getFilterNames(merchandiseFilters?.typeFilter), [merchandiseFilters?.typeFilter]);
	const availabilityItems = useMemo(
		() => getFilterNames(merchandiseFilters?.availabilityFilter),
		[merchandiseFilters?.availabilityFilter]
	);
	const brandAndProductLine = useMemo<BrandWithProductLines[]>(
		() =>
			merchandiseFilters?.brandAndProductlinesFilter
				?.map((b: models["BrandAndProductlineFilterDto"]) => ({
					id: b.id ?? "",
					name: b.name ?? "",
					productLines: (
						b.productLines?.map((p: models["FilterOptionDto"]) => ({
							id: p.id ?? "",
							name: p.name ?? ""
						})) ?? []
					).sort((a, b) => a.name.localeCompare(b.name))
				}))
				.sort((a, b) => a.name.localeCompare(b.name)) ?? [],
		[merchandiseFilters]
	);
	const filters = useMemo(
		() => [
			{
				selectedItems: filterOptions?.channel ?? [],
				onChange: (v: string[]) => onFilterChange("channel", v),
				filterType: FilterType.CHANNEL,
				items: channelItems,
				isSearchable: false,
				dataTestId: getFilterId("channel"),
				GTMIdentifier: getFilterId("channel")
			},
			{
				selectedItems: filterOptions?.category ?? [],
				onChange: (v: string[]) => onFilterChange("category", v),
				filterType: FilterType.CATEGORY,
				items: categoryItems,
				isSearchable: true,
				dataTestId: getFilterId("category"),
				GTMIdentifier: getFilterId("category")
			},
			{
				selectedItems: filterOptions?.type ?? [],
				onChange: (v: string[]) => onFilterChange("type", v),
				filterType: FilterType.TYPE,
				items: typeItems,
				isSearchable: true,
				dataTestId: getFilterId("type"),
				GTMIdentifier: getFilterId("type")
			},
			{
				selectedItems: filterOptions?.availability ?? [],
				onChange: (v: string[]) => onFilterChange("availability", v),
				filterType: FilterType.AVAILABILITY,
				items: availabilityItems,
				isSearchable: false,
				dataTestId: getFilterId("availability"),
				GTMIdentifier: getFilterId("availability")
			}
		],
		[
			filterOptions?.channel,
			filterOptions?.category,
			filterOptions?.type,
			filterOptions?.availability,
			onFilterChange,
			channelItems,
			categoryItems,
			typeItems,
			availabilityItems
		]
	);

	const sortProducts = (event: SelectChangeEvent) => {
		const nextFilterOptions = {
			...filterOptions,
			sort: event.target.value as SortOption
		};

		setFilterOptions(nextFilterOptions);
		onFiltersChanged(nextFilterOptions);
	};

	return (
		<Style.MarketingMaterialsFiltersContainer>
			{filters.slice(0, 1).map(
				(filter) =>
					channelItems.length > 0 && (
						<ChipDropdownMenu
							key={filter.filterType}
							selectedItems={filter.selectedItems}
							onChange={filter.onChange}
							filterType={filter.filterType}
							items={filter.items}
							dataTestId={filter.dataTestId}
							GTMIdentifier={filter.GTMIdentifier}
							isSearchable={filter.isSearchable}
						/>
					)
			)}
			<MarketingMaterialsBrandProductLineFilter
				selectedItems={filterOptions?.brandAndProductLine ?? []}
				onChange={(v: string[]) => onFilterChange("brandAndProductLine", v)}
				dataTestId={getFilterId("brand-and-product-line")}
				GTMIdentifier={getFilterId("brand-and-product-line")}
				brandAndProductLine={brandAndProductLine}
			/>
			{filters.slice(1).map((filter) => (
				<ChipDropdownMenu
					key={filter.filterType}
					selectedItems={filter.selectedItems}
					onChange={filter.onChange}
					filterType={filter.filterType}
					items={filter.items}
					dataTestId={filter.dataTestId}
					GTMIdentifier={filter.GTMIdentifier}
					isSearchable={filter.isSearchable}
				/>
			))}
			<Style.SortFormControl size="small">
				<InputLabel id="marketing-materials-sort-by-label">Sort By</InputLabel>
				<Select
					labelId="marketing-materials-select-small-label"
					id="marketing-materials-select-small"
					label="Sort By"
					value={filterOptions.sort}
					onChange={sortProducts}
				>
					{SORT_OPTIONS.map((option) => (
						<MenuItem
							key={option.value}
							value={option.value}
						>
							{option.label}
						</MenuItem>
					))}
				</Select>
			</Style.SortFormControl>
		</Style.MarketingMaterialsFiltersContainer>
	);
};

export default MarketingMaterialsFilters;
